/**
 * Plan média Black Friday / Noël 2026 (« BF 26 ») : cibles de MER et de CPA.
 *
 * Chaque tranche du plan porte un budget pub, un nombre de commandes et un CA
 * produits HT. Les cibles d'une période quelconque se calculent au prorata des
 * jours qu'elle partage avec chaque tranche :
 *   budget  = Σ jours communs × budget de la tranche ÷ jours de la tranche
 *   (idem pour commandes et CA)
 *   CPA cible = budget ÷ commandes ; MER cible = CA ÷ budget.
 * Sur une tranche entière on retombe sur les chiffres du plan ; sur tout le
 * plan, sur la ligne Total (57 €, ×2,56).
 *
 * Verdict : atteint → vert ; manqué de moins de `TOLERANCE` → orange ; au-delà
 * → rouge.
 */

import type { StatusLevel } from '@shared/types/stats';

export interface Bf26Slice {
  label: string;
  /** Bornes incluses, YYYY-MM-DD. */
  from: string;
  to: string;
  budget: number;
  orders: number;
  revenueHt: number;
}

export const BF26_PLAN: readonly Bf26Slice[] = [
  { label: '1–15 nov.', from: '2026-11-01', to: '2026-11-15', budget: 20_000, orders: 333, revenueHt: 52_500 },
  { label: '16–22 nov. · teasing', from: '2026-11-16', to: '2026-11-22', budget: 10_000, orders: 154, revenueHt: 24_200 },
  { label: '23–30 nov. · Black Friday', from: '2026-11-23', to: '2026-11-30', budget: 77_000, orders: 1_400, revenueHt: 177_600 },
  { label: '1–21 déc. · Noël', from: '2026-12-01', to: '2026-12-21', budget: 108_000, orders: 1_895, revenueHt: 298_500 },
  { label: '22–31 déc.', from: '2026-12-22', to: '2026-12-31', budget: 10_000, orders: 143, revenueHt: 22_500 },
];

/** Écart toléré avant de passer au rouge : 10 % sous la cible. */
export const BF26_TOLERANCE = 0.1;

const DAY = 86_400_000;
const dayOf = (iso: string) => Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
const daysBetween = (from: number, to: number) => Math.round((to - from) / DAY) + 1;

export interface Bf26Targets {
  /** Tranches touchées par la période, avec le nombre de jours communs. */
  slices: Array<{ slice: Bf26Slice; days: number }>;
  budget: number;
  orders: number;
  revenueHt: number;
  cpa: number;
  mer: number;
  /** Jours de la période hors plan (avant le 1er nov. ou après le 31 déc.). */
  daysOutside: number;
}

/** Cibles pour la période [from, to] (incluses). `null` si elle ne touche pas le plan. */
export function bf26Targets(from: string, to: string): Bf26Targets | null {
  const lo = dayOf(from);
  const hi = dayOf(to);
  if (!(hi >= lo)) return null;
  const slices: Bf26Targets['slices'] = [];
  let budget = 0;
  let orders = 0;
  let revenueHt = 0;
  let inside = 0;
  for (const slice of BF26_PLAN) {
    const sLo = dayOf(slice.from);
    const sHi = dayOf(slice.to);
    const start = Math.max(lo, sLo);
    const end = Math.min(hi, sHi);
    if (end < start) continue;
    const days = daysBetween(start, end);
    const share = days / daysBetween(sLo, sHi);
    slices.push({ slice, days });
    budget += slice.budget * share;
    orders += slice.orders * share;
    revenueHt += slice.revenueHt * share;
    inside += days;
  }
  if (slices.length === 0 || budget <= 0 || orders <= 0) return null;
  return {
    slices,
    budget,
    orders,
    revenueHt,
    cpa: budget / orders,
    mer: revenueHt / budget,
    daysOutside: daysBetween(lo, hi) - inside,
  };
}

/** Niveau d'un indicateur face à sa cible. */
export function bf26Level(actual: number, target: number, higherIsBetter: boolean): StatusLevel {
  if (higherIsBetter) {
    if (actual >= target) return 'good';
    return actual >= target * (1 - BF26_TOLERANCE) ? 'warning' : 'critical';
  }
  if (actual <= target) return 'good';
  return actual <= target * (1 + BF26_TOLERANCE) ? 'warning' : 'critical';
}
